import React, { useState } from 'react'
import styled from '@emotion/styled'
import { HiExternalLink } from 'react-icons/hi'
import { Section } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import {
  ActionCard,
  OutlineButton,
  OpenLeatButton,
  SidePanel,
  SidePanelTitle,
  SidePanelBody
} from './styles'
import LoadingPrimaryButton from '../Common/LoadingPrimaryButton'
import { LEAT_APP_URL } from '../../constants'
import {
  AUTH_STEP_TITLE,
  AUTH_STEP_DESCRIPTION,
  EXTERNAL_LINK_PROPS
} from './copy'
import StepLayout from '../Common/StepLayout'
import PatField from './PatField'

interface TokenRejectedStepProps {
  token: string
  loading?: boolean
  onTryAgain: (token: string) => void
  onCancel: () => void
}

const CAUSES = [
  'The token was copied partially, or with a trailing space',
  "The Slerp app isn't installed in your Leat marketplace yet",
  'The token has been revoked or has expired in Leat'
]

const TokenRejectedStep = ({
  token: initialToken,
  loading,
  onTryAgain,
  onCancel
}: TokenRejectedStepProps) => {
  const [token, setToken] = useState(initialToken)

  return (
    <StepLayout
      sidebar={
        <SidePanel>
          <SidePanelTitle>Still stuck?</SidePanelTitle>
          <SidePanelBody>
            Generate a fresh token in Leat under Auth and paste it here. If that
            fails too, send us the account name you&apos;re connecting and
            we&apos;ll check from our side.
          </SidePanelBody>
          <OpenLeatButton href={LEAT_APP_URL} {...EXTERNAL_LINK_PROPS}>
            OPEN LEAT <HiExternalLink />
          </OpenLeatButton>
        </SidePanel>
      }
    >
      <Section
        title={AUTH_STEP_TITLE}
        description={AUTH_STEP_DESCRIPTION}
        stepStatus={StepStatus.active}
        stepNumber={1}
      >
        <Callout>
          <CalloutTitle>
            We couldn&apos;t connect to Leat with that token
          </CalloutTitle>
          <CalloutBody>
            Nothing has changed on your account. Check the three most common
            causes below and try again.
          </CalloutBody>
          {CAUSES.map((cause) => (
            <CalloutBody key={cause}>· {cause}</CalloutBody>
          ))}
        </Callout>

        <PatField
          id='leat-pat-retry'
          invalid
          value={token}
          onChange={setToken}
        />
      </Section>

      <ActionCard>
        <OutlineButton onClick={onCancel}>CANCEL</OutlineButton>
        <LoadingPrimaryButton
          disabled={!token.trim() || loading}
          loading={loading}
          onClick={() => onTryAgain(token.trim())}
        >
          TRY AGAIN
        </LoadingPrimaryButton>
      </ActionCard>
    </StepLayout>
  )
}

const Callout = styled.div`
  background: #fff0f0;
  border: 1px solid #a40000;
  border-radius: 6px;
  padding: 8px 16px;
  color: #a40000;

  display: flex;
  flex-direction: column;
  gap: 4px;
`

const CalloutTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  margin: 0;
`

const CalloutBody = styled.p`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
`

export default TokenRejectedStep
